import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Label } from '@/components/ui/label'
import { useProjectCreateWizard } from '../ProjectCreateWizardContext'
import { ProjectCreateNavigation } from '../ProjectCreateNavigation'
import { useClientJobSitesV2 } from '@/hooks/useClientJobSitesV2'

export function Step2ExistingClientJobSite({
  onNext,
  onBack,
  currentStep,
  customerId,
  clientDisplayName,
}: {
  onNext: () => void
  onBack: () => void
  currentStep: number
  customerId: string
  clientDisplayName: string
}) {
  const { draft, setSelectedJobSiteId, setSelectedJobSiteLabel, setIsNewJobSite } = useProjectCreateWizard()

  const { jobSites, isLoading, error } = useClientJobSitesV2({ customerId, take: 50 })

  const canGoNext = draft.isNewJobSite || !!draft.selectedJobSiteId

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold">Pour quel chantier est ce projet ?</h2>
        <p className="text-muted-foreground">Client : {clientDisplayName}</p>
      </div>

      <div className="max-w-2xl mx-auto space-y-6">
        {jobSites.length > 1 && (
          <div className="rounded-lg border bg-muted/30 p-4">
            <p className="text-sm text-muted-foreground">⚠️ Ce client a plusieurs chantiers</p>
          </div>
        )}

        {error && (
          <div className="rounded-lg border bg-muted/30 p-4">
            <p className="text-sm text-muted-foreground">Erreur lors du chargement des chantiers</p>
          </div>
        )}

        <div className="space-y-2">
          <RadioGroup
            value={draft.isNewJobSite ? '__new__' : draft.selectedJobSiteId || ''}
            onValueChange={(v) => {
              if (v === '__new__') {
                setSelectedJobSiteId(null)
                setSelectedJobSiteLabel(null)
                setIsNewJobSite(true)
                return
              }
              setIsNewJobSite(false)
              setSelectedJobSiteId(v)
              const label = jobSites.find((js) => (js.jobSiteId || js.id) === v)?.label || null
              setSelectedJobSiteLabel(label)
            }}
            className="space-y-3"
          >
            {isLoading ? (
              <div className="rounded-lg border bg-muted/30 p-4">
                <p className="text-sm text-muted-foreground">Chargement...</p>
              </div>
            ) : (
              jobSites.map((js) => {
                const jsId = js.jobSiteId || js.id || ''
                if (!jsId) return null

                const id = `jobsite_${jsId}`
                const isSelected = !draft.isNewJobSite && draft.selectedJobSiteId === jsId

                return (
                  <div
                    key={jsId}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      setIsNewJobSite(false)
                      setSelectedJobSiteId(jsId)
                      setSelectedJobSiteLabel(js.label || null)
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        setIsNewJobSite(false)
                        setSelectedJobSiteId(jsId)
                        setSelectedJobSiteLabel(js.label || null)
                      }
                    }}
                    className={
                      'border rounded-lg px-4 py-3 cursor-pointer transition-colors ' +
                      (isSelected ? 'border-primary bg-primary/5' : 'border-border hover:border-primary')
                    }
                  >
                    <div className="flex items-start gap-3">
                      <RadioGroupItem value={jsId} id={id} className="mt-1" />
                      <div className="min-w-0 flex-1">
                        <Label htmlFor={id} className="font-medium">
                          🏠 {js.label || 'Chantier'}
                        </Label>
                        {js.address && <p className="text-sm text-muted-foreground">{js.address}</p>}
                      </div>
                    </div>
                  </div>
                )
              })
            )}

            <div
              data-testid="jobsite-option-new"
              role="button"
              tabIndex={0}
              onClick={() => {
                setSelectedJobSiteId(null)
                setSelectedJobSiteLabel(null)
                setIsNewJobSite(true)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  setSelectedJobSiteId(null)
                  setSelectedJobSiteLabel(null)
                  setIsNewJobSite(true)
                }
              }}
              className={
                'border rounded-lg px-4 py-3 cursor-pointer transition-colors ' +
                (draft.isNewJobSite ? 'border-primary bg-primary/5' : 'border-border hover:border-primary')
              }
            >
              <div className="flex items-start gap-3">
                <RadioGroupItem value="__new__" id="jobsite_new" className="mt-1" />
                <Label htmlFor="jobsite_new" className="font-medium">
                  Nouveau chantier (autre adresse)
                </Label>
              </div>
            </div>
          </RadioGroup>
        </div>

        <ProjectCreateNavigation currentStep={currentStep} onBack={onBack} onNext={onNext} canGoNext={canGoNext} />
      </div>
    </div>
  )
}
